<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  minLength,
  requiredIf,
  minValue,
} from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { formatBytes } from 'shared/helpers/FileHelper';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import ContactAPI from 'dashboard/api/contacts';
import { WHATSAPP_EVOLUTION_CAMPAIGN_MESSAGE_CHARACTER_LIMIT } from 'shared/constants/campaign';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const MAX_EVOLUTION_ATTACHMENTS = 1;

const getInitialState = () => ({
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
  message: '',
  intervalMinSeconds: 20,
  intervalMaxSeconds: 45,
  showAdvanced: false,
  attachments: [],
});

const state = reactive(getInitialState());
const templateParserRef = ref(null);
const recipientsCount = ref(0);
const attachmentInputRef = ref(null);
const isUploadingAttachment = ref(false);
let lastAudienceRequestId = 0;

// Derived timing helpers for Evolution advanced options
const minTotalSeconds = computed(
  () =>
    Number(recipientsCount.value - 1 || 0) *
    Number(state.intervalMinSeconds || 0)
);
const maxTotalSeconds = computed(
  () =>
    Number(recipientsCount.value - 1 || 0) *
    Number(state.intervalMaxSeconds || 0)
);
const formatDuration = secs => {
  const total = Math.max(0, Math.floor(Number(secs || 0)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const selectedInbox = computed(() =>
  formState.inboxes.value?.find(i => i.id === state.inboxId)
);
const isEvolutionProvider = computed(
  () => selectedInbox.value?.provider === 'evolution'
);
const isTemplateProviderSelected = computed(
  () => !!state.inboxId && selectedInbox.value?.provider !== 'evolution'
);

const rules = computed(() => ({
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required: requiredIf(() => isTemplateProviderSelected.value) },
  scheduledAt: { required },
  selectedAudience: { required },
  message: {
    required: requiredIf(
      () => isEvolutionProvider.value && state.attachments.length === 0
    ),
  },
  intervalMinSeconds: {
    required: requiredIf(() => isEvolutionProvider.value),
    minValue: minValue(0),
    // Block values over 10 minutes (600 seconds)
    lteTenMin: value => !isEvolutionProvider.value || Number(value || 0) <= 600,
    // Ensure range is at least 20s: min <= max - 20
    lteMaxMinusThirty: value =>
      !isEvolutionProvider.value ||
      Number(value || 0) <= Number(state.intervalMaxSeconds || 0) - 20,
  },
  intervalMaxSeconds: {
    required: requiredIf(() => isEvolutionProvider.value),
    // Require at least 20 seconds for max
    minValue: minValue(20),
    // Block values over 10 minutes (600 seconds)
    lteTenMin: value => !isEvolutionProvider.value || Number(value || 0) <= 600,
    // Ensure range is at least 20s: max >= min + 20
    gteMinPlusThirty: value =>
      !isEvolutionProvider.value ||
      Number(value || 0) >= Number(state.intervalMinSeconds || 0) + 20,
  },
}));

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const selectedLabelTitles = computed(() => {
  const ids = new Set(state.selectedAudience || []);
  return (formState.labels.value || [])
    .filter(l => ids.has(l.id))
    .map(l => l.title);
});

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId || isEvolutionProvider.value) return [];

  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const evolutionMessageCharacterSummary = computed(
  () =>
    `${(state.message || '').length} / ${
      WHATSAPP_EVOLUTION_CAMPAIGN_MESSAGE_CHARACTER_LIMIT
    }`
);

const formErrors = computed(() => ({
  title: v$.value.title.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.ERROR')
    : '',
  inbox: v$.value.inboxId.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.ERROR')
    : '',
  template: v$.value.templateId.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.ERROR')
    : '',
  scheduledAt: v$.value.scheduledAt.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.ERROR')
    : '',
  audience: v$.value.selectedAudience.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ERROR')
    : '',
  intervalMinSeconds: v$.value.intervalMinSeconds?.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MIN_ERROR')
    : '',
  intervalMaxSeconds: v$.value.intervalMaxSeconds?.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MAX_ERROR')
    : '',
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, getInitialState());
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  if (isEvolutionProvider.value) {
    return {
      title: state.title,
      message: state.message,
      inbox_id: state.inboxId,
      scheduled_at: formatToUTCString(state.scheduledAt),
      template_params: {
        interval_min_seconds: Number(state.intervalMinSeconds || 0),
        interval_max_seconds: Number(state.intervalMaxSeconds || 0),
      },
      audience: state.selectedAudience?.map(id => ({ id, type: 'Label' })),
      attachments: state.attachments.map(attachment => attachment.blobId),
    };
  }
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({ id, type: 'Label' })),
    attachments: [],
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    state.message = '';
    state.intervalMinSeconds = 20;
    state.intervalMaxSeconds = 45;
    state.showAdvanced = false;
    state.attachments = [];
  }
);

// Fetch recipients count when audience changes (OR across labels)
const fetchRecipientsCount = async () => {
  const titles = selectedLabelTitles.value;
  if (!titles || titles.length === 0) {
    recipientsCount.value = 0;
    return;
  }
  // eslint-disable-next-line no-plusplus
  const requestId = ++lastAudienceRequestId;
  try {
    const payload = titles.map((title, idx) => ({
      attribute_key: 'labels',
      filter_operator: 'equal_to',
      values: [title],
      attribute_model: 'standard',
      ...(idx !== titles.length - 1 ? { query_operator: 'or' } : {}),
    }));
    const {
      data: { meta },
    } = await ContactAPI.filter(undefined, 'name', { payload });
    if (requestId === lastAudienceRequestId) {
      recipientsCount.value = Number(meta?.count || 0);
    }
  } catch (_) {
    if (requestId === lastAudienceRequestId) {
      recipientsCount.value = 0;
    }
  }
};

watch(selectedLabelTitles, fetchRecipientsCount, { immediate: true });

const canAddMoreAttachments = computed(
  () => state.attachments.length < MAX_EVOLUTION_ATTACHMENTS
);

const handleAttachmentButtonClick = () => {
  if (!canAddMoreAttachments.value) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.LIMIT_ALERT'));
    return;
  }

  attachmentInputRef.value?.click?.();
};

const handleAttachmentChange = async event => {
  const files = event?.target?.files;
  const file = files && files[0];
  if (!file) return;

  if (!canAddMoreAttachments.value) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.LIMIT_ALERT'));
    if (event?.target) event.target.value = '';
    return;
  }

  isUploadingAttachment.value = true;
  try {
    const { blobId } = await uploadFile(file);
    state.attachments.push({
      blobId,
      name: file.name,
      size: file.size,
      type: file.type,
    });
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.UPLOAD_ERROR'));
  } finally {
    isUploadingAttachment.value = false;
    if (event?.target) event.target.value = '';
  }
};

const removeAttachmentAt = index => {
  state.attachments.splice(index, 1);
};

// Alerts for thresholds: min ≥ 570s and max ≥ 600s
watch(
  () => state.intervalMinSeconds,
  (value, oldValue) => {
    if (
      isEvolutionProvider.value &&
      Number(value || 0) >= 570 &&
      Number(oldValue || 0) < 570
    ) {
      useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MIN_THRESHOLD_ALERT'));
    }
    if (
      isEvolutionProvider.value &&
      Number(value || 0) < 10 &&
      Number(oldValue || 0) >= 10
    ) {
      useAlert(
        t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MIN_TEN_LOWER_BOUND_ALERT')
      );
    }
    if (
      isEvolutionProvider.value &&
      Number(value || 0) < 0 &&
      Number(oldValue || 0) >= 0
    ) {
      useAlert(
        t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MIN_LOWER_BOUND_ALERT')
      );
    }
  }
);
watch(
  () => state.intervalMaxSeconds,
  (value, oldValue) => {
    if (
      isEvolutionProvider.value &&
      Number(value || 0) >= 600 &&
      Number(oldValue || 0) < 600
    ) {
      useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.EXCEED_ALERT'));
    }
    if (
      isEvolutionProvider.value &&
      Number(value || 0) < 20 &&
      Number(oldValue || 0) >= 20
    ) {
      useAlert(
        t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MAX_LOWER_BOUND_ALERT')
      );
    }
  }
);

// Alert if range becomes less than 30s (max - min < 30)
watch(
  () => state.intervalMinSeconds,
  (value, oldValue) => {
    if (!isEvolutionProvider.value) return;
    const max = Number(state.intervalMaxSeconds || 0);
    const newDiff = max - Number(value || 0);
    const oldDiff = max - Number(oldValue || 0);
    if (oldDiff >= 30 && newDiff < 30) {
      useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.RANGE_ALERT'));
    }
  }
);
watch(
  () => state.intervalMaxSeconds,
  (value, oldValue) => {
    if (!isEvolutionProvider.value) return;
    const min = Number(state.intervalMinSeconds || 0);
    const newDiff = Number(value || 0) - min;
    const oldDiff = Number(oldValue || 0) - min;
    if (oldDiff >= 30 && newDiff < 30) {
      useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.RANGE_ALERT'));
    }
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between mb-0.5">
        <label for="audience" class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
        </label>
        <span
          v-if="selectedLabelTitles.length"
          class="text-xs font-normal text-n-slate-11"
        >
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.RECIPIENTS_COUNT', {
              count: recipientsCount,
            })
          }}
        </span>
      </div>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :max-visible-options="3"
        inline-dropdown
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :max-visible-options="3"
        inline-dropdown
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Evolution: free text message -->
    <div v-if="isEvolutionProvider" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.LABEL') }}
        </label>
        <textarea
          v-model="state.message"
          rows="4"
          :maxlength="WHATSAPP_EVOLUTION_CAMPAIGN_MESSAGE_CHARACTER_LIMIT"
          class="w-full mb-1 rounded-md border border-n-weak bg-n-alpha-black2 p-3 text-sm outline-none focus:border-n-slate-8"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.PLACEHOLDER')"
        />
        <div class="flex justify-end text-xs text-n-slate-10">
          {{ evolutionMessageCharacterSummary }}
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.LABEL') }}
        </label>
        <div class="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            color="slate"
            variant="ghost"
            icon="i-lucide-paperclip"
            :label="
              isUploadingAttachment
                ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.UPLOADING')
                : t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.UPLOAD')
            "
            :disabled="isUploadingAttachment"
            class="!w-fit"
            @click="handleAttachmentButtonClick"
          />
          <span v-if="!canAddMoreAttachments" class="text-xs text-n-amber-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.LIMIT_REACHED') }}
          </span>
        </div>
        <ul v-if="state.attachments.length" class="flex flex-col gap-2">
          <li
            v-for="(attachment, index) in state.attachments"
            :key="`${attachment.blobId}-${index}`"
            class="flex items-center justify-between gap-3 rounded-md border border-n-weak bg-n-alpha-black1 px-3 py-2"
          >
            <div class="flex flex-col">
              <span class="text-sm font-medium text-n-slate-12">
                {{ attachment.name }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ formatBytes(attachment.size || 0) }}
              </span>
            </div>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              color="slate"
              icon="i-lucide-trash"
              @click="removeAttachmentAt(index)"
            />
          </li>
        </ul>
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.HELP_TEXT') }}
        </p>
        <input
          ref="attachmentInputRef"
          type="file"
          class="hidden"
          @change="handleAttachmentChange"
        />
      </div>

      <div>
        <button
          class="flex pl-0 items items-center gap-1 text-sm text-n-slate-12 hover:underline"
          type="button"
          @click="state.showAdvanced = !state.showAdvanced"
        >
          <span class="i-lucide-plus size-3" />
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ADVANCED_OPTIONS.LABEL') }}
        </button>
        <div v-if="state.showAdvanced" class="mt-2">
          <div class="grid grid-cols-2 gap-3">
            <Input
              v-model.number="state.intervalMinSeconds"
              type="number"
              min="0"
              max="600"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MIN_LABEL')"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MIN_PLACEHOLDER')
              "
              :message="formErrors.intervalMinSeconds"
              :message-type="formErrors.intervalMinSeconds ? 'error' : 'info'"
            />
            <Input
              v-model.number="state.intervalMaxSeconds"
              type="number"
              min="0"
              max="600"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MAX_LABEL')"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.MAX_PLACEHOLDER')
              "
              :message="formErrors.intervalMaxSeconds"
              :message-type="formErrors.intervalMaxSeconds ? 'error' : 'info'"
            />
          </div>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INTERVAL.INFO') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Cloud/others: require template -->
    <div v-if="isTemplateProviderSelected" class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :max-visible-options="3"
        inline-dropdown
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>

      <!-- Template Parser -->
      <WhatsAppTemplateParser
        v-if="selectedTemplate"
        ref="templateParserRef"
        :template="selectedTemplate"
      />
    </div>

    <!-- Evolution advanced summary at the end of form -->
    <div
      v-if="isEvolutionProvider && state.showAdvanced"
      class="mt-2 p-3 rounded-md border border-n-weak bg-n-alpha-black1 text-n-slate-12"
    >
      <div class="flex items-center justify-between text-sm">
        <span>{{
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SUMMARY.DELAY_LABEL')
        }}</span>
        <span>
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SUMMARY.DELAY_RANGE', {
              min: Number(state.intervalMinSeconds || 0),
              max: Number(state.intervalMaxSeconds || 0),
            })
          }}
        </span>
      </div>
      <div
        v-if="recipientsCount > 0"
        class="flex items-center justify-between text-sm mt-1"
      >
        <span>{{
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SUMMARY.TOTAL_LABEL')
        }}</span>
        <span>
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.SEND_SUMMARY.TOTAL_RANGE', {
              min: formatDuration(minTotalSeconds),
              max: formatDuration(maxTotalSeconds),
            })
          }}
        </span>
      </div>
    </div>

    <div
      class="flex flex-col sm:flex-row gap-3 justify-between items-stretch sm:items-center w-full"
    >
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
